import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { ReturnRequest } from '../types';
import './Returns.css';

const Returns: React.FC = () => {
  const { error: showError } = useToast();
  const [returns, setReturns] = useState<ReturnRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchReturns = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<ReturnRequest[]>('/returns');
      setReturns(response.data);
    } catch (error) {
      console.error('Failed to fetch returns:', error);
      showError('Failed to load return requests');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchReturns();
  }, [fetchReturns]);

  const getStatusColor = (status?: string): string => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'var(--color-success)';
      case 'rejected':
        return 'var(--color-danger)';
      case 'processing':
        return 'var(--color-teal)';
      case 'completed':
        return 'var(--color-success)';
      default:
        return 'var(--text-secondary)';
    }
  };

  if (loading) {
    return <div className="loading">Loading return requests...</div>;
  }

  return (
    <div className="returns-page">
      <div className="container">
        <h1 className="page-title">My Returns</h1>

        {returns.length === 0 ? (
          <div className="no-returns">
            <p>You haven't submitted any return requests yet.</p>
          </div>
        ) : (
          <div className="returns-list">
            {returns.map((returnRequest) => (
              <div key={returnRequest.id} className="return-card">
                <div className="return-header">
                  <div>
                    <h3>Return Request #{returnRequest.rmaNumber || returnRequest.id}</h3>
                    <p className="return-date">
                      Submitted on {new Date(returnRequest.requestDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="return-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(returnRequest.status) }}
                    >
                      {returnRequest.status}
                    </span>
                  </div>
                </div>

                <div className="return-details">
                  <div className="return-item">
                    <h4>Order #{returnRequest.order?.orderNumber || returnRequest.orderId}</h4>
                    <p><strong>Product:</strong> {returnRequest.orderItem?.product?.name}</p>
                    <p><strong>Quantity:</strong> {returnRequest.quantity}</p>
                    <p><strong>Reason:</strong> {returnRequest.reason}</p>
                  </div>

                  {returnRequest.refundAmount && returnRequest.refundAmount > 0 && (
                    <div className="refund-amount">
                      <p><strong>Refund Amount:</strong> ${returnRequest.refundAmount.toFixed(2)}</p>
                    </div>
                  )}

                  {returnRequest.resolution && (
                    <div className="return-resolution">
                      <p><strong>Resolution:</strong> {returnRequest.resolution}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Returns;

